import { Constants } from './common/Constants';
import * as Enums from './common/Enumerations';
import { isError } from './common/errors/ErrorUtil';
import { InitControllerError } from './common/errors/InitControllerError';
import * as InternalTypes from './common/InternalTypes';
import { Logger } from './common/Logger';
import { LoggerTexts } from './common/LoggerTexts';
import { ScopeCheck } from './common/ScopeCheck';
import * as Types from './common/Types';
import { CentralConfig } from './config/CentralConfig';
import { ConfigJsonParser } from './config/ConfigJsonParser';
import { ConfigurationLoader } from './config/ConfigurationLoader';
import { ConfigUtil } from './config/ConfigUtil';
import { ControllerProvider } from './ControllerProvider';
import { QualtricsBridge } from './QualtricsBridge';
import { StorageService } from './storage/StorageService';
import { DateCalculator } from './survey/DateCalculator';

export class InitController {
	private readonly _logger: Logger;
	private _centralConfig: CentralConfig;
	private _controllerProvider: ControllerProvider;
	private _pushConfigStatus: InternalTypes.PushConfigStatus;

	constructor() {
		this._logger = new Logger(Constants.FILE_INIT_CONTROLLER);
	}

	get controllerProvider(): ControllerProvider {
		return this._controllerProvider;
	}

	get centralConfig(): CentralConfig {
		return this._centralConfig;
	}

	get qualtricsBridge(): QualtricsBridge {
		return this._controllerProvider.qualtricsBridge;
	}

	get pushConfigStatus(): InternalTypes.PushConfigStatus {
		return this._pushConfigStatus;
	}

	public prepareInit(
		tenantInfo: Types.TenantInfo,
		configIdentifier?: Types.ConfigIdentifier,
		configJson?: Types.JSONValue
	): InternalTypes.Result<Enums.API_INIT_MODE> {
		const initParamValidationResult = this.areMandatoryInitParametersValid(tenantInfo);
		if (isError(initParamValidationResult)) {
			return initParamValidationResult;
		}

		return this.identifyInitMode(configIdentifier, configJson);
	}

	public areMandatoryInitParametersValid(tenantInfo: Types.TenantInfo): InternalTypes.Result<boolean> {
		if (!tenantInfo) {
			return {
				success: false,
				error: new InitControllerError(undefined, LoggerTexts.INIT_CONTROLLER.INIT_MANDATORY_PARAMS_NOT_VALID_TENANT_INFO)
			};
		}
		if (!tenantInfo.tenantId) {
			return {
				success: false,
				error: new InitControllerError(undefined, LoggerTexts.INIT_CONTROLLER.INIT_MANDATORY_PARAMS_NOT_VALID_TENANT_ID)
			};
		}
		if (!tenantInfo.tenantRole) {
			return {
				success: false,
				error: new InitControllerError(undefined, LoggerTexts.INIT_CONTROLLER.INIT_MANDATORY_PARAMS_NOT_VALID_TENANT_ROLE)
			};
		}
		this._logger.info(LoggerTexts.INIT_CONTROLLER.INIT_MANDATORY_PARAMS_VALID);
		return { success: true, result: true };
	}

	public identifyInitMode(configIdentifier?: Types.ConfigIdentifier, configJson?: Types.JSONValue): InternalTypes.Result<Enums.API_INIT_MODE> {
		if (!configIdentifier && !configJson) {
			return {
				success: false,
				error: new InitControllerError(undefined, LoggerTexts.INIT_CONTROLLER.INIT_CFG_ID_OR_CFG_JSON_NEED_TO_BE_PROVIDED)
			};
		} else if (configIdentifier && configJson) {
			return {
				success: false,
				error: new InitControllerError(undefined, LoggerTexts.INIT_CONTROLLER.INIT_BOTH_CFG_IDENTIFIER_CFG_JSON_NOT_POSSIBLE)
			};
		} else if (configIdentifier && !configJson) {
			const configIdentifierValidationResult = ConfigUtil.isConfigIdentifierIsValid(configIdentifier);
			if (isError(configIdentifierValidationResult)) {
				return configIdentifierValidationResult;
			}
			this._logger.info(`Init Mode set to: automatic.`);
			return { success: true, result: Enums.API_INIT_MODE.automatic };
		}
		//Defaulting to only remaining combination (!configIdentifier && configJson) otherwise coverage cannot be reached as not reachable
		this._logger.info(`Init Mode set to: manual.`);
		return { success: true, result: Enums.API_INIT_MODE.manual };
	}

	public async loadConfiguration(
		initMode: Enums.API_INIT_MODE,
		tenantInfo: Types.TenantInfo,
		configIdentifier?: Types.ConfigIdentifier,
		configJson?: Types.JSONValue
	): Promise<InternalTypes.Result<boolean>> {
		let initConfigResult: InternalTypes.Result<{
			centralConfig: CentralConfig;
			pushConfigStatus: InternalTypes.PushConfigStatus;
		}> = {
			success: false,
			error: new InitControllerError(undefined, LoggerTexts.INIT_CONTROLLER.INIT_CFG_LOAD_FAILED)
		};
		if (initMode === Enums.API_INIT_MODE.automatic && configIdentifier) {
			initConfigResult = await this._initWithCentralConfig(tenantInfo, configIdentifier);
		} else if (initMode === Enums.API_INIT_MODE.manual && configJson) {
			initConfigResult = ConfigJsonParser.parseJsonToCentralConfig(tenantInfo, configJson);
		}
		if (isError(initConfigResult)) {
			return initConfigResult;
		}
		this._centralConfig = initConfigResult.result.centralConfig;
		this._pushConfigStatus = initConfigResult.result.pushConfigStatus;

		ConfigUtil.checkBaseConfiguration(this._centralConfig);

		return { success: true, result: true };
	}

	public hasAnyScopeItemConfigured(): boolean {
		if (ScopeCheck.hasAnyScopeItemConfigured(this._centralConfig.startupConfig)) {
			return true;
		}
		return false;
	}

	public determineRuntimeScope(isSurveyInvitationDialogCallbackProvided: boolean): InternalTypes.RuntimeScope {
		const runtimeScope = { manual: false, timedPush: false, appPush: false, instantPush: false } as InternalTypes.RuntimeScope;
		if (!isSurveyInvitationDialogCallbackProvided) {
			this._logger.info(LoggerTexts.INIT_CONTROLLER.SURVEY_INVITATION_DIALOG_CALLBACK_NOT_PROVIDED);
			return runtimeScope;
		}
		if (this._isAppPushPossible()) {
			runtimeScope.appPush = true;
		}
		if (this._isTimedPushPossible()) {
			runtimeScope.timedPush = true;
		}
		if (this._isInstantPushPossible()) {
			runtimeScope.instantPush = true;
		}
		return runtimeScope;
	}

	public async initFeatures(
		runtimeScope: InternalTypes.RuntimeScope,
		surveyInvitationDialogCallback?: Types.SurveyInvitationDialogCallback
	): Promise<InternalTypes.Result<boolean>> {
		this._controllerProvider = new ControllerProvider(this._centralConfig);
		this.controllerProvider.initStorage();
		this._ensureUserStateIsValid(this.controllerProvider.storageService);
		if (runtimeScope.timedPush || runtimeScope.appPush || runtimeScope.instantPush) {
			this._controllerProvider.initValidationHandler();
			this._setupInitialPushDates(this.controllerProvider.storageService, runtimeScope);
			if (surveyInvitationDialogCallback) {
				this._controllerProvider.initPresentationController(surveyInvitationDialogCallback);
				if (runtimeScope.timedPush) {
					this._controllerProvider.initTimedPushController(runtimeScope);
				}
				if (runtimeScope.instantPush) {
					this._controllerProvider.initInstantPushController(runtimeScope);
				}
			} else {
				this._logger.warn(LoggerTexts.INIT_CONTROLLER.INIT_PUSH_FEATURES_NOT_POSSIBLE);
			}
		}
		await this.controllerProvider.initTheming();
		return await this._tryLoadQualtricsApi();
	}

	private _ensureUserStateIsValid(storage: StorageService) {
		if (storage) {
			const userState = storage.readUserState();
			if (userState) {
				const isTestModeActive = ScopeCheck.isTestModeActive();
				if (isTestModeActive !== userState.isDebugState) {
					storage.saveUserState(storage.getInitialUserState());
				}
			}
		}
	}

	public setInitialContextData() {
		this.controllerProvider.contextDataController.setDeviceContext();
		const sessionContextData = {
			tenantId: this.centralConfig.tenantInfo.tenantId,
			tenantRole: this.centralConfig.tenantInfo.tenantRole,
			productName: this.centralConfig.startupConfig.productName,
			platformType: this.centralConfig.startupConfig.platformType
		} as InternalTypes.SessionContextData;
		this.controllerProvider.contextDataController.setSessionContext(sessionContextData);
	}

	private async _initWithCentralConfig(
		tenantInfo: Types.TenantInfo,
		configIdentifier: Types.ConfigIdentifier
	): Promise<InternalTypes.Result<{ centralConfig: CentralConfig; pushConfigStatus: InternalTypes.PushConfigStatus }>> {
		const configurationLoader = new ConfigurationLoader(configIdentifier, tenantInfo);
		return configurationLoader.loadConfiguration();
	}
	private _isAppPushPossible(): boolean {
		if (ScopeCheck.isAppPushAvailable(this._centralConfig.startupConfig)) {
			if (this.pushConfigStatus.appPushConfigValidationSuccess) {
				ConfigUtil.checkAppPushConfigValuesLimits(this._centralConfig.pushConfig!);
				return true;
			} else {
				this._logger.warn(LoggerTexts.INIT_CONTROLLER.APP_PUSH_SCOPE_SET_INCONSISTENT);
			}
		}
		return false;
	}

	private _isTimedPushPossible(): boolean {
		if (ScopeCheck.isTimedPushAvailable(this._centralConfig.startupConfig)) {
			if (this.pushConfigStatus.timedPushConfigValidationSuccess) {
				ConfigUtil.checkTimedPushConfigValuesLimits(this._centralConfig.pushConfig!);
				return true;
			} else {
				this._logger.warn(LoggerTexts.INIT_CONTROLLER.TIMED_PUSH_SCOPE_SET_INCONSISTENT);
			}
		}
		return false;
	}

	private _isInstantPushPossible(): boolean {
		if (ScopeCheck.isInstantPushAvailable(this._centralConfig.startupConfig)) {
			if (this.pushConfigStatus.instantPushConfigValidationSuccess) {
				return true;
			} else {
				this._logger.warn(LoggerTexts.INIT_CONTROLLER.INSTANT_PUSH_SCOPE_SET_INCONSISTENT);
			}
		}
		return false;
	}

	private _setupInitialPushDates(storage: StorageService, runtimeScope: InternalTypes.RuntimeScope) {
		const userState = storage.readUserState();
		const pushConfig = this._centralConfig.pushConfig;
		if (userState && pushConfig) {
			const timedPushDate = userState.timedPushDate;
			const appPushDate = userState.appPushDate;
			let dirty = false;
			if (!timedPushDate && runtimeScope.timedPush) {
				const initialAppPushDate = DateCalculator.calculateInitialTimedPushDate(pushConfig);
				userState.timedPushDate = initialAppPushDate;
				dirty = true;
			}
			if (!appPushDate && runtimeScope.appPush) {
				const initialAppPushDate = DateCalculator.calculateInitialAppPushDate(pushConfig);
				userState.appPushDate = initialAppPushDate;
				dirty = true;
			}
			if (dirty) {
				storage.saveUserState(userState);
			}
		}
	}

	private async _tryLoadQualtricsApi(): Promise<InternalTypes.Result<boolean>> {
		const qualtricsUri = this._centralConfig.startupConfig.qualtricsInternalUri;
		const runtimeConfig = this._centralConfig.runtimeConfig;
		this.qualtricsBridge.registerCSPExceptionEvent();
		if (!(await this.qualtricsBridge.canLoadQualtricsScript(qualtricsUri))) {
			return { success: false, error: new InitControllerError(undefined, LoggerTexts.INIT_CONTROLLER.INIT_QUALTRICS_URI_CANNOT_BE_REACHED) };
		}
		if (runtimeConfig && !runtimeConfig.useApi && runtimeConfig.domElementId) {
			this.qualtricsBridge.renderQualtricsButton(runtimeConfig.domElementId);
		}
		const apiLoaded = this.qualtricsBridge.registerApiLoadedEvent();
		const qualtricScriptRendered = await this.qualtricsBridge.renderQualtricsScript(
			qualtricsUri,
			this._centralConfig.startupConfig.enableQualtricsRestrictedMode
		);
		this.qualtricsBridge.deregisterQualtricsScriptLoadedEvent(qualtricsUri);
		if (!qualtricScriptRendered) {
			return { success: false, error: new InitControllerError(undefined, LoggerTexts.INIT_CONTROLLER.RENDER_QUALTRICS_SCRIPT_FAILED) };
		}
		const isApiLoaded = await apiLoaded;
		if (this.qualtricsBridge.isCSPExceptionOccurred) {
			return { success: false, error: new InitControllerError(undefined, LoggerTexts.INIT_CONTROLLER.INIT_QUALTRICS_LOADING_FAILED) };
		}
		if (!isApiLoaded) {
			return { success: false, error: new InitControllerError(undefined, LoggerTexts.INIT_CONTROLLER.INIT_QUALTRICS_LOADING_FAILED) };
		}
		return { success: true, result: isApiLoaded };
	}
}
